import {
  Box,
  Button,
  Container,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  Input,
  NumberInput,
  NumberInputField,
  Text,
} from "@chakra-ui/react";
import AdminDataTable from "components/AdminDataTable/AdminDataTable";
import { useState } from "react";
import { queryClient } from "App";
import { convertParamsToString } from "helpers/stringManipulation";
import { patchRequest, postRequest, useMutationWrapper, useQueryWrapper } from "services/apiHelper";
import { languageRequest } from "services/request";
import { queryKeys } from "services/querykeys";
import { type LanguageType } from "utils/constants";
import { successToast } from "utils/hooks";

// Stable row identity for the shared table.
const languageRowId = (language: LanguageType): string => language.id;

const emptyForm = {
  name: "",
  iso6391: "",
  iso6393: "",
  aliases: "",
  sortOrder: "500",
};

const getLanguagePayload = (formData: typeof emptyForm) => {
  const aliases = formData.aliases
    .split(",")
    .map((alias) => alias.trim())
    .filter(Boolean);
  const sortOrder = Number(formData.sortOrder);

  return {
    name: formData.name.trim(),
    ...(formData.iso6391.trim() ? { iso6391: formData.iso6391.trim() } : {}),
    ...(formData.iso6393.trim() ? { iso6393: formData.iso6393.trim() } : {}),
    ...(aliases.length > 0 ? { aliases } : { aliases: [] }),
    ...(Number.isNaN(sortOrder) ? {} : { sortOrder }),
  };
};

const LanguageCatalogue = () => {
  const [formData, setFormData] = useState(emptyForm);
  const [editingLanguageId, setEditingLanguageId] = useState<string | null>(null);
  const { data, isLoading } = useQueryWrapper(queryKeys.LANGUAGES, languageRequest.LANGUAGES, {
    staleTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const resetForm = () => {
    setFormData(emptyForm);
    setEditingLanguageId(null);
  };

  const onSaveSuccess = () => {
    void queryClient.invalidateQueries(queryKeys.LANGUAGES);
    successToast(editingLanguageId ? "Language updated" : "Language created");
    resetForm();
  };

  const onDisableSuccess = () => {
    void queryClient.invalidateQueries(queryKeys.LANGUAGES);
    successToast("Language disabled");
  };

  const { mutate: createLanguage, isLoading: isCreating } = useMutationWrapper(
    postRequest,
    onSaveSuccess,
  );
  const { mutate: updateLanguage, isLoading: isUpdating } = useMutationWrapper(
    patchRequest,
    onSaveSuccess,
  );
  const { mutate: disableLanguage, isLoading: isDisabling } = useMutationWrapper(
    patchRequest,
    onDisableSuccess,
  );

  const languages: LanguageType[] = data?.data ?? [];

  const handleChange = (event) => {
    setFormData({
      ...formData,
      [event.target.name]: event.target.value,
    });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const payload = getLanguagePayload(formData);

    if (editingLanguageId) {
      updateLanguage({
        url: convertParamsToString(languageRequest.LANGUAGE, { id: editingLanguageId }),
        data: payload,
      });
      return;
    }

    createLanguage({
      url: languageRequest.LANGUAGES,
      data: payload,
    });
  };

  const handleEdit = (language: LanguageType) => {
    setEditingLanguageId(language.id);
    setFormData({
      name: language.name,
      iso6391: language.iso6391 ?? "",
      iso6393: language.iso6393 ?? "",
      aliases: (language.aliases ?? []).join(", "),
      sortOrder: String(language.sortOrder ?? 500),
    });
  };

  const handleDisable = (language: LanguageType) => {
    const shouldDisable = window.confirm(`Disable ${language.name}?`);
    if (!shouldDisable) return;

    disableLanguage({
      url: convertParamsToString(languageRequest.LANGUAGE, { id: language.id }),
      data: { active: false },
    });
  };

  const COLUMNS = [
    { Header: "Name", accessor: "name" },
    {
      Header: "Aliases",
      Cell: ({ cell }: any) => {
        const aliases: string[] = cell.row.original?.aliases ?? [];
        return <Text>{aliases.length > 0 ? aliases.join(", ") : "-"}</Text>;
      },
    },
    {
      Header: "ISO",
      Cell: ({ cell }: any) => {
        const iso = [cell.row.original?.iso6391, cell.row.original?.iso6393].filter(Boolean);
        return <Text>{iso.length > 0 ? iso.join(" / ") : "-"}</Text>;
      },
    },
    { Header: "Sort", accessor: "sortOrder", align: "right" as const, minWidth: 70 },
    {
      Header: "Actions",
      align: "right" as const,
      minWidth: 180,
      Cell: ({ cell }: any) => (
        <>
          <Button
            size="sm"
            mr="2"
            onClick={() => {
              handleEdit(cell.row.original);
            }}
          >
            Edit
          </Button>
          <Button
            size="sm"
            variant="secondary"
            isLoading={isDisabling}
            onClick={() => {
              handleDisable(cell.row.original);
            }}
          >
            Disable
          </Button>
        </>
      ),
    },
  ];

  return (
    <Container mt="4" maxW="container.xl">
      <Flex
        alignItems={{ base: "stretch", md: "center" }}
        justifyContent="space-between"
        flexDir={{ base: "column", md: "row" }}
        gap="3"
        mb="4"
      >
        <Box>
          <Text as="h1" fontSize="1.3rem" color="#4e6e97" textTransform="uppercase">
            Languages
          </Text>
          <Text fontSize="sm" color="#5f6f7f">
            Active catalogue rows are shown here. Disabled languages stay attached to existing
            scripts but are hidden from selectors.
          </Text>
        </Box>
        {editingLanguageId && (
          <Button variant="secondaryOutline" onClick={resetForm}>
            Cancel edit
          </Button>
        )}
      </Flex>

      <Box borderWidth="1px" borderColor="#dbe3e9" p="4" mb="6">
        <form onSubmit={handleSubmit}>
          <Grid templateColumns={{ base: "repeat(1, 1fr)", lg: "2fr 1fr 1fr 1fr" }} gap="4">
            <FormControl isRequired>
              <FormLabel>Name</FormLabel>
              <Input name="name" value={formData.name} onChange={handleChange} />
            </FormControl>
            <FormControl>
              <FormLabel>ISO 639-1</FormLabel>
              <Input
                name="iso6391"
                value={formData.iso6391}
                onChange={handleChange}
                maxLength={2}
              />
            </FormControl>
            <FormControl>
              <FormLabel>ISO 639-3</FormLabel>
              <Input
                name="iso6393"
                value={formData.iso6393}
                onChange={handleChange}
                maxLength={3}
              />
            </FormControl>
            <FormControl>
              <FormLabel>Sort order</FormLabel>
              <NumberInput
                value={formData.sortOrder}
                min={0}
                onChange={(value) => {
                  setFormData({ ...formData, sortOrder: value });
                }}
              >
                <NumberInputField name="sortOrder" />
              </NumberInput>
            </FormControl>
          </Grid>
          <FormControl mt="4">
            <FormLabel>Aliases</FormLabel>
            <Input
              name="aliases"
              value={formData.aliases}
              onChange={handleChange}
              placeholder="Comma-separated aliases"
            />
            <FormHelperText m={0}>
              Aliases are used by backend migration and matching.
            </FormHelperText>
          </FormControl>
          <Button mt="4" type="submit" isLoading={isCreating || isUpdating}>
            {editingLanguageId ? "Update language" : "Create language"}
          </Button>
        </form>
      </Box>

      <AdminDataTable
        ariaLabel="Languages"
        columns={COLUMNS}
        data={languages}
        emptyState={
          <Text color="#7f94a6" my="4">
            No active languages found.
          </Text>
        }
        getRowId={languageRowId}
        loading={isLoading}
      />
    </Container>
  );
};

export default LanguageCatalogue;
